import _ from 'lodash';
import { FetchResult } from '@apollo/client';
import React, { useState } from 'react';
import { Formik, FormikHelpers, FormikProps } from 'formik';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import aOrAn from 'indefinite';

// Material UI
import Typography from '@material-ui/core/Typography';

// Data
import { IEditAssessmentSessionContainer, IEditAssessmentSessionFields } from './edit-assessment-session.interface'
import { ICreateSkillFields } from '../../Skill/CreateSkill/create-skill.interface';

// Components
import { EditAssessmentSession } from './edit-assessment-session.component';
import { getMessages } from './edit-assessment-session.messages';
import { IntlMsg } from '../../_core/IntlMsg/intl-msg.component';
import { useCompanySportTerminology } from '../../../lib/stores/company.store';
import { SportTerminology } from '../../../lib/types';
import { pluralTerm } from '../../../lib/helpers/term.helper';
import { IceConflictBanner } from '../ice-conflict-warning.component';

export const EditAssessmentSessionContainer: React.FC<IEditAssessmentSessionContainer> = ({
  assessmentSession,
  updateAssessmentSession,
  createAgeGroup,
  ageGroups,
  ageGroupsLoading,
  ageGroupsError
}) => {
  const termLookup = useCompanySportTerminology()
  const facilityTerm = termLookup?.[SportTerminology.FACILITY] || 'Facility'
  const sessionTerm = termLookup?.[SportTerminology.SESSION] || 'Session'

  const [submitError, setSubmitError] = useState<React.ReactNode>(null);
  const messages = getMessages(termLookup);
  const [showSaved, setShowSaved] = useState(false);

  const INITIAL_FORM_VALUES: IEditAssessmentSessionFields = {
    location: assessmentSession?.location || '',
    area: assessmentSession?.area || '',
    address: assessmentSession?.address || '',
    date: assessmentSession?.date || dayjs().add(1, 'day').format('YYYY/MM/DD'),
    start: assessmentSession?.start || 60 * 12,
    duration: assessmentSession?.duration || 60,
    isAttendanceRequested: Boolean(assessmentSession?.isAttendanceRequested),
  };

  if (assessmentSession && assessmentSession.gender) {
    INITIAL_FORM_VALUES.gender = { value: assessmentSession.gender, label: assessmentSession.gender };
  }

  if (assessmentSession && assessmentSession.ageGroup) {
    INITIAL_FORM_VALUES.ageGroupId = { value: assessmentSession.ageGroup._id, label: assessmentSession.ageGroup.name };
  }

  async function handleSubmit(values: IEditAssessmentSessionFields, { setSubmitting, resetForm }: FormikHelpers<IEditAssessmentSessionFields>) {
    setSubmitError(null);

    updateAssessmentSession({
      ..._.omit(values, ['ageGroupId', 'gender', 'duration']),
      duration: Number(values.duration),
      gender: values.gender?.value,
      ageGroupId: values.ageGroupId?.value
    }, assessmentSession?.isPlayerNotificationEnabled === true && window.confirm(`Are you sure you want to send ${aOrAn(sessionTerm.toLowerCase())} update to all ${pluralTerm(termLookup?.[SportTerminology.ATHLETE]?.toLowerCase())}?`))
      .then(() => {
        setSubmitting(false);
        setShowSaved(true);
        setTimeout(() => {
          setShowSaved(false);
        }, 2000);

        resetForm({ values });
      })
      .catch((error: unknown) => {
        console.error('updateAssessmentSession failed:', error);
        setSubmitError(<IntlMsg {...messages.errSaveFailed} />);
        setSubmitting(false);
      });
  }

  const validationSchema = Yup.object().shape({
    location: Yup.string().required(`Enter the ${facilityTerm?.toLowerCase()} name for the ${sessionTerm}`),
    address: Yup.string().required(`Enter the address of the ${sessionTerm}`),
    date: Yup.string().required(`Start date is required`),
    start: Yup.string().required('Start time is required'),
    duration: Yup.string().required('Duration is required'),
    ageGroupId: Yup.object().nullable().required('Select or create an age group'),
  });

  const ageGroupOptions = (ageGroups || []).map((group: IAgeGroup) => ({
    value: group?._id || '',
    label: group?.name || 'Unknown'
  }));
  const handleCreateAgeGroup = (form: FormikProps<ICreateSkillFields>, name: string) => {
    createAgeGroup(name).then((result: FetchResult) => {
      if (result.data && result.data.createAgeGroup) {
        form.setFieldValue('ageGroupId', { value: result.data.createAgeGroup._id, label: name });
        form.setFieldTouched('ageGroupId');
      }
    });
  }

  if (!ageGroups.length && !ageGroupsLoading && ageGroupsError) return (
    <Typography>Something went wrong. Please try again later</Typography>
  );

  return (
    <>
    <IceConflictBanner conflicts={assessmentSession?.iceConflicts} />
    <Formik
      enableReinitialize
      initialValues={INITIAL_FORM_VALUES}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {form => <EditAssessmentSession
        form={form}
        submitError={submitError}
        showSaved={showSaved}
        createAgeGroup={handleCreateAgeGroup}
        ageGroupOptions={ageGroupOptions}
        ageGroupsLoading={ageGroupsLoading}
        assessmentSession={assessmentSession}
      />}
    </Formik>
    </>
  );
};

